import { api } from '@/lib/api/client';
import type { BreachStatus, RiskLevel } from '@shared/enums/gdpr.enum';

import { buildQuery } from './types';

// ─── Treatment Register ──────────────────────────────────────

export interface TreatmentRecord {
  id: number;
  name: string;
  purpose: string;
  legalBasis: string;
  dataCategories: string[];
  recipients: string[];
  retentionPeriod: string;
  dpiaRequired: boolean;
  dpiaDate?: string | null;
  lastReviewedAt?: string | null;
  version: number;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTreatmentDto {
  name: string;
  purpose: string;
  legalBasis: string;
  dataCategories: string[];
  recipients: string[];
  retentionPeriod: string;
  dpiaRequired: boolean;
  dpiaDate?: string | null;
  notes?: string | null;
}

export type UpdateTreatmentDto = Partial<CreateTreatmentDto>;

export interface PaginatedTreatments {
  data: TreatmentRecord[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export async function getTreatmentsApi(params?: { page?: number; limit?: number }): Promise<PaginatedTreatments> {
  return api.get(`/admin/gdpr/treatments${buildQuery(params as Record<string, unknown>)}`);
}

export async function getTreatmentApi(id: number): Promise<TreatmentRecord> {
  return api.get(`/admin/gdpr/treatments/${id}`);
}

export async function createTreatmentApi(data: CreateTreatmentDto): Promise<TreatmentRecord> {
  return api.post('/admin/gdpr/treatments', data);
}

export async function updateTreatmentApi(id: number, data: UpdateTreatmentDto): Promise<TreatmentRecord> {
  return api.patch(`/admin/gdpr/treatments/${id}`, data);
}

export async function deleteTreatmentApi(id: number): Promise<void> {
  return api.delete(`/admin/gdpr/treatments/${id}`);
}

export async function reviewTreatmentApi(id: number): Promise<TreatmentRecord> {
  return api.post(`/admin/gdpr/treatments/${id}/review`, {});
}

export async function exportTreatmentsApi(): Promise<TreatmentRecord[]> {
  return api.get('/admin/gdpr/treatments/export');
}

// ─── Breach Register ─────────────────────────────────────────

export interface DataBreach {
  id: number;
  detectedAt: string;
  occurredAt?: string | null;
  breachType: string;
  cause: string;
  description: string;
  affectedData: string[];
  affectedCount?: number | null;
  riskLevel: string;
  status: string;
  cnilNotificationRequired: boolean;
  cnilNotifiedAt?: string | null;
  cnilReference?: string | null;
  subjectsNotifiedAt?: string | null;
  containmentActions?: string | null;
  lessonsLearned?: string | null;
  closedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBreachDto {
  detectedAt: string;
  occurredAt?: string | null;
  breachType: string;
  cause: string;
  description: string;
  affectedData: string[];
  affectedCount?: number | null;
  riskLevel: string;
  cnilNotificationRequired: boolean;
  containmentActions?: string | null;
}

export type UpdateBreachDto = Partial<CreateBreachDto & { lessonsLearned: string | null; status: string }>;

export interface PaginatedBreaches {
  data: DataBreach[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface GetBreachesParams {
  page?: number;
  limit?: number;
  status?: BreachStatus;
  riskLevel?: RiskLevel;
}

export async function getBreachesApi(params?: GetBreachesParams): Promise<PaginatedBreaches> {
  return api.get(`/admin/gdpr/breaches${buildQuery(params as Record<string, unknown>)}`);
}

export async function getBreachApi(id: number): Promise<DataBreach> {
  return api.get(`/admin/gdpr/breaches/${id}`);
}

export async function createBreachApi(data: CreateBreachDto): Promise<DataBreach> {
  return api.post('/admin/gdpr/breaches', data);
}

export async function updateBreachApi(id: number, data: UpdateBreachDto): Promise<DataBreach> {
  return api.patch(`/admin/gdpr/breaches/${id}`, data);
}

export async function notifyBreachCnilApi(id: number, cnilReference: string): Promise<DataBreach> {
  return api.post(`/admin/gdpr/breaches/${id}/notify-cnil`, { cnilReference });
}

export async function notifyBreachSubjectsApi(id: number, method: string): Promise<DataBreach> {
  return api.post(`/admin/gdpr/breaches/${id}/notify-subjects`, { method });
}

export async function closeBreachApi(id: number): Promise<DataBreach> {
  return api.post(`/admin/gdpr/breaches/${id}/close`, {});
}

export async function exportBreachesApi(): Promise<DataBreach[]> {
  return api.get('/admin/gdpr/breaches/export');
}
